import {getMedias} from '@cohort/merchants/apps';
import {useMedias} from '@cohort/merchants/apps/useMedias';
import type {AspectRatio} from '@cohort/merchants/components/form/input/fileInput/FileInput';
import LocalizedInput from '@cohort/merchants/components/form/input/LocalizedInput';
import {RadioCardIconBadge, RadioCards} from '@cohort/merchants/components/form/RadioCards';
import SwitchInput from '@cohort/merchants/components/form/SwitchInput';
import LocalizedTextEditorInput from '@cohort/merchants/components/form/textEditor/LocalizedTextEditorInput';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import {useCohortForm} from '@cohort/merchants/hooks/contexts/form';
import type {CreateEditStepModalType} from '@cohort/merchants/pages/campaigns/campaign/edit/settings/challenge/utils';
import {MediaKindSchema} from '@cohort/shared/apps';
import {CohortVideoMediaSpec} from '@cohort/shared/apps/cohort/medias/video';
import {ChallengeStepAllowedMediaKinds} from '@cohort/shared/schema/common/challengeStep';
import {getVideoUrl} from '@cohort/shared/utils/media';
import {cn} from '@cohort/shared-frontend/utils/classNames';
import {isLandscapeVideo} from '@cohort/shared-frontend/utils/isFile';
import {isFile} from '@cohort/shared-frontend/utils/isFile';
import {Prohibit} from '@phosphor-icons/react';
import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {isDefined, isString} from 'remeda';

const StepStatementInput: React.FC = () => {
  const [aspectRatio, setAspectRatio] = useState<AspectRatio | undefined>();
  const {t} = useTranslation('apps', {
    keyPrefix: 'common.stepStatementInput',
  });
  const merchant = useCurrentMerchant();
  const {register, control, watch, setValue, setFormAssets} =
    useCohortForm<CreateEditStepModalType>();

  useEffect(() => {
    setFormAssets(assets => [
      ...(assets ?? []),
      {
        name: 'statementMedia.config.imageFileKey',
        type: 'contentImage',
      },
      {
        name: 'statementMedia.config.videoFileKey',
        type: 'contentVideo',
      },
      {
        name: 'statementMedia.config.thumbnailFileKey',
        type: 'contentVideoThumbnail',
      },
    ]);
  }, [setFormAssets]);

  const {getMediaTitle, getMedia} = useMedias();

  const medias = getMedias();

  const [selectedLanguage, statementEnabled, statementMedia] = watch([
    'selectedLanguage',
    'statementEnabled',
    'statementMedia',
  ]);

  const isDefaultLanguageSelected = selectedLanguage === merchant.defaultLanguage;

  const mediaKind = MediaKindSchema.safeParse(statementMedia?.kind);

  const selectedMedia = mediaKind.success ? getMedia(mediaKind.data) : null;

  const radioCardsOptions = useMemo(() => {
    const noMedia = {
      prefix: <RadioCardIconBadge icon={<Prohibit size={24} className="text-slate-400" />} />,
      label: t('titleNoMedia'),
      value: 'no-media',
    };

    const allowed = medias
      .filter(media => ChallengeStepAllowedMediaKinds.includes(media.spec.kind))
      .map(media => ({
        prefix: <RadioCardIconBadge icon={<media.icon size={24} textColor="text-slate-400" />} />,
        label: getMediaTitle(media),
        value: media.spec.kind,
      }));

    return [noMedia, ...allowed];
  }, [getMediaTitle, medias, t]);

  const getCustomAspectRatio = useCallback(async (): Promise<AspectRatio | undefined> => {
    if (
      selectedMedia?.spec.kind === CohortVideoMediaSpec.kind &&
      (isString(statementMedia?.config.videoFileKey) || isFile(statementMedia?.config.videoFileKey))
    ) {
      const videoFileOrSrc = isString(statementMedia.config.videoFileKey)
        ? getVideoUrl(import.meta.env.COHORT_ENV, statementMedia.config.videoFileKey)
        : statementMedia.config.videoFileKey;
      const isLandscape = await isLandscapeVideo(videoFileOrSrc);
      return isLandscape ? 'aspect-video' : 'aspect-auto';
    }
  }, [statementMedia?.config.videoFileKey, selectedMedia?.spec.kind]);

  useEffect(() => {
    getCustomAspectRatio().then(setAspectRatio);
  }, [getCustomAspectRatio, statementMedia?.config.videoFileKey]);

  return (
    <Fragment>
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-col gap-1">
          <label className="block text-sm font-medium text-slate-700">{t('titleStatement')}</label>
          <label className="text-xs text-slate-500">{t('subtitleStatement')}</label>
        </div>
        <div className="w-10">
          <SwitchInput name="statementEnabled" register={register} control={control} />
        </div>
      </div>

      {statementEnabled && (
        <Fragment>
          <LocalizedInput
            type="text"
            label={t('labelTitle')}
            name="statementTitle"
            register={register}
            control={control}
            selectedLanguage={selectedLanguage}
            optional={!isDefaultLanguageSelected}
          />

          <LocalizedTextEditorInput
            label={t('labelDescription')}
            name="statementDescription"
            register={register}
            control={control}
            selectedLanguage={selectedLanguage}
            optional
          />

          <RadioCards
            name="statementMedia.kind"
            direction="row"
            label={t('labelMediaKind')}
            className="grid grid-cols-4"
            register={register}
            control={control}
            onChange={() => setValue('statementMedia.config', {})}
            options={radioCardsOptions}
          />
          <div
            className={cn(
              isDefined(statementMedia?.config.videoFileKey) &&
                selectedMedia?.spec.kind === CohortVideoMediaSpec.kind &&
                aspectRatio !== 'aspect-video' &&
                'mx-auto w-1/4'
            )}
          >
            {selectedMedia && (
              <selectedMedia.configComponent
                name="statementMedia"
                customAspectRatio={aspectRatio}
              />
            )}
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};
export default StepStatementInput;
